import CloseIcon from "@mui/icons-material/Close";
import DSAutocomplete from "../../system-design/DSAutocomplete";
import DSBox from "../../system-design/DSBox";
import DSIconButton from "../../system-design/DSIconButton";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSTextField from "../../system-design/DSTextField";
import DSTypography from "../../system-design/DSTypography";
import {
  SYNCED_SETTINGS,
  SettingFilter,
  SyncedSetting,
  settingByField,
} from "../syncedSettings";

const FILTERABLE_SETTINGS = SYNCED_SETTINGS.filter(
  (setting) => setting.filterable,
);

const defaultMark = (isDefault: boolean) => (isDefault ? " (default)" : "");

const FilterValueInput = ({
  setting,
  value,
  onChange,
}: {
  setting: SyncedSetting;
  value: string;
  onChange: (value: string) => void;
}) => {
  if (setting.type === "number") {
    const [min = "", max = ""] = value.split("~");
    const bound = (
      label: string,
      current: string,
      other: string,
      isMin: boolean,
    ) => (
      <DSTextField
        type="text"
        size="small"
        placeholder={label}
        inputProps={{ inputMode: "decimal" }}
        sx={{ width: "80px" }}
        value={current}
        onChange={(e) => {
          const next = e.target.value.trim();
          onChange(isMin ? `${next}~${other}` : `${other}~${next}`);
        }}
      />
    );
    return (
      <>
        {bound("min", min, max, true)}
        <DSTypography color="text.secondary">to</DSTypography>
        {bound("max", max, min, false)}
        <DSTypography color="text.secondary">
          (0-{setting.max}, unset = {setting.default})
        </DSTypography>
      </>
    );
  }

  const options =
    setting.type === "boolean"
      ? [
          {
            value: "true",
            label: `On${defaultMark(setting.default)}`,
          },
          {
            value: "false",
            label: `Off${defaultMark(!setting.default)}`,
          },
        ]
      : setting.type === "enum"
        ? setting.options.map((option) => ({
            value: option,
            label: `${option}${defaultMark(option === setting.default)}`,
          }))
        : [];

  return (
    <DSTextField
      select
      size="small"
      sx={{ minWidth: "180px" }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <DSMenuItem key={option.value} value={option.value}>
          {option.label}
        </DSMenuItem>
      ))}
    </DSTextField>
  );
};

type SettingsFilterProps = {
  filters: SettingFilter[];
  onChange: (filters: SettingFilter[]) => void;
};

/**
 * Filters the Devices table by synced settings. A clock that never wrote a
 * setting matches its default value.
 */
const SettingsFilter = ({ filters, onChange }: SettingsFilterProps) => {
  const usedFields = new Set(filters.map(({ field }) => field));
  const available = FILTERABLE_SETTINGS.filter(
    (setting) => !usedFields.has(setting.field),
  );

  return (
    <DSBox
      sx={{
        flexBasis: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {filters.map(({ field, value }) => {
        const setting = settingByField(field);
        if (!setting) return null;
        return (
          <DSBox
            key={field}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            flexWrap={"wrap"}
          >
            <DSTypography sx={{ minWidth: "180px" }}>
              {setting.label}:
            </DSTypography>
            <FilterValueInput
              setting={setting}
              value={value}
              onChange={(next) =>
                onChange(
                  filters.map((filter) =>
                    filter.field === field ? { field, value: next } : filter,
                  ),
                )
              }
            />
            <DSIconButton
              size="small"
              title="Remove filter"
              onClick={() =>
                onChange(filters.filter((filter) => filter.field !== field))
              }
            >
              <CloseIcon fontSize="small" />
            </DSIconButton>
          </DSBox>
        );
      })}
      <DSAutocomplete
        size="small"
        sx={{ width: 300 }}
        options={available}
        groupBy={(setting) => setting.section}
        getOptionLabel={(setting) => setting.label}
        value={null}
        blurOnSelect
        onChange={(_, setting) => {
          if (setting)
            onChange([...filters, { field: setting.field, value: "" }]);
        }}
        renderInput={(params) => (
          <DSTextField {...params} label="Filter by synced setting" />
        )}
      />
    </DSBox>
  );
};

export default SettingsFilter;
